/*
 * This file is part of glowingblue/orell-bookshelf.
 *
 * Copyright (c) 2021 Priska Wermelinger & Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Modal, { type IInternalModalAttrs } from 'flarum/common/components/Modal';
import Switch from 'flarum/common/components/Switch';
import Button from 'flarum/common/components/Button';
import Stream from 'flarum/common/utils/Stream';
import type Mithril from 'mithril';
import type Bookshelf from '../../common/models/Bookshelf';

export interface EditBookshelfModalAttrs extends IInternalModalAttrs {
	bookshelf: Bookshelf | null;
	onsubmit: (bookshelf: Bookshelf) => void;
}

export default class EditBookshelfModal<CustomAttrs extends EditBookshelfModalAttrs = EditBookshelfModalAttrs> extends Modal<CustomAttrs> {
	protected bookshelf!: Bookshelf;

	protected name!: Stream<string>;

	protected isPrivate!: Stream<boolean>;

	oninit(vnode: Mithril.Vnode<CustomAttrs, this>) {
		super.oninit(vnode);

		const { bookshelf } = this.attrs;
		this.bookshelf = bookshelf || app.store.createRecord<Bookshelf>('bookshelves');

		this.name = Stream(this.bookshelf.name() || '');
		// A record that does not exist yet carries no `isPrivate` attribute.
		const isPrivate = this.bookshelf.isPrivate() as boolean | undefined;
		this.isPrivate = Stream(isPrivate === undefined ? false : isPrivate);
	}

	title(): Mithril.Children {
		const { bookshelf } = this.attrs;

		return app.translator.trans(
			bookshelf && bookshelf.exists ? 'glowingblue-orell-bookshelf.forum.modal.edit_title' : 'glowingblue-orell-bookshelf.forum.modal.create_title'
		);
	}

	className(): string {
		return 'EditBookshelfModal Modal Modal--small';
	}

	content(): Mithril.Children {
		return [
			<div className="Modal-body">
				<div className="EditBookshelfModal-form">
					<div className="Form-group">
						<label className="label">{app.translator.trans('glowingblue-orell-bookshelf.forum.modal.name_label')}</label>
						<input type="text" name="name" className="FormControl" bidi={this.name} />
					</div>

					<div className="Form-group">
						<Switch state={this.isPrivate() || false} onchange={this.isPrivate}>
							{app.translator.trans('glowingblue-orell-bookshelf.forum.modal.isPrivate_label')}
						</Switch>
					</div>

					<div className="Form-group">
						<Button type="submit" className="Button Button--primary EditBookshelfModal-SubmitButton">
							{app.translator.trans('glowingblue-orell-bookshelf.forum.modal.submit_label')}
						</Button>
					</div>
				</div>
			</div>,
		];
	}

	onsubmit(e: SubmitEvent) {
		e.preventDefault();

		if (this.name() === '') {
			alert(app.translator.trans('glowingblue-orell-bookshelf.forum.modal.include_name'));
			return;
		}

		this.bookshelf.pushAttributes({
			name: this.name(),
			isPrivate: this.isPrivate(),
		});

		this.attrs.onsubmit(this.bookshelf);

		app.modal.close();
	}
}
